import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { requireAuth } from "~/lib/auth-guard";
import { orpc } from "~/lib/orpc-client";
import { queryClient } from "~/lib/query-client";
import { Skeleton, useToast } from "@alchema/ui";
import { CandidateScreeningBadge } from "~/components/CandidateScreeningBadge";
import { RouteErrorBoundary } from "~/components/RouteErrorBoundary";
import {
  ANNEX_III_AUDIT_EVENTS,
  logAnnexIIIAuditEvent,
} from "~/lib/annex-iii-audit";
import * as m from "~/paraglide/messages";

export const Route = createFileRoute("/deep-match")({
  beforeLoad: async () => {
    await requireAuth();
  },
  loader: async () => {
    await Promise.all([
      queryClient.prefetchQuery(orpc.job.list.queryOptions()),
      queryClient.prefetchQuery(orpc.resume.list.queryOptions()),
    ]);
    return null;
  },
  component: DeepMatchPage,
  pendingComponent: DeepMatchSkeleton,
  errorComponent: RouteErrorBoundary,
});

/* ── Skeleton ── */

function DeepMatchSkeleton() {
  return (
    <div className="animate-fade-up mx-auto max-w-5xl" aria-busy="true" role="status" aria-label={m.deep_match_loading_aria()}>
      <div className="page-header">
        <div>
          <Skeleton className="h-7 w-48" />
          <Skeleton className="mt-2 h-4 w-72" />
        </div>
      </div>
      <div className="mt-6 flex flex-col gap-4 sm:flex-row">
        <Skeleton className="h-9 flex-1 rounded-md" />
        <Skeleton className="h-9 flex-1 rounded-md" />
        <Skeleton className="h-9 w-32 rounded-md" />
      </div>
    </div>
  );
}

/* ── Helpers ── */

function clamp(v: number) {
  return Math.max(0, Math.min(100, Math.round(v)));
}

function scoreColor(score: number): string {
  if (score >= 85) return "var(--success)";
  if (score >= 70) return "var(--warning)";
  return "var(--error)";
}

function severityDot(severity: string): string {
  if (severity === "blocker") return "var(--error)";
  if (severity === "significant") return "var(--warning)";
  return "var(--text-muted)";
}

function matchStrengthColor(strength: string): string {
  if (strength === "strong") return "var(--success)";
  if (strength === "partial") return "var(--warning)";
  return "var(--error)";
}

/* ── Empty State ── */

function EmptyState() {
  return (
    <div
      className="mt-12 flex flex-col items-center py-16 text-center"
      role="status"
    >
      <p className="text-sm font-medium" style={{ color: "var(--text)" }}>
        No analysis yet
      </p>
      <p className="mt-1 text-sm" style={{ color: "var(--text-muted)" }}>
        Select a job and resume above, then run the analysis.
      </p>
    </div>
  );
}

/* ── Score Display ── */

function ScoreDisplay({ score }: { score: number }) {
  const s = clamp(score);
  const color = scoreColor(s);

  return (
    <div className="flex items-baseline gap-3">
      <span
        className="font-semibold tabular-nums leading-none"
        style={{ fontSize: "28px", color }}
      >
        {s}
      </span>
      <span className="text-sm" style={{ color: "var(--text-muted)" }}>
        {m.deep_match_score_max()}
      </span>
      <span
        className="inline-block h-2 w-2 rounded-full"
        style={{ backgroundColor: color }}
        aria-hidden="true"
      />
    </div>
  );
}

/* ── Dimension Row ── */

function DimensionRow({ label, score, weight, details }: {
  label: string;
  score: number;
  weight: number;
  details: string[];
}) {
  const [expanded, setExpanded] = React.useState(false);
  const s = clamp(score);
  const color = scoreColor(s);

  return (
    <div
      className="py-3 cursor-pointer"
      style={{ borderBottom: "1px solid var(--border-subtle)" }}
      onClick={() => setExpanded(!expanded)}
      onKeyDown={(e) => e.key === "Enter" && setExpanded(!expanded)}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
    >
      <div className="flex items-center gap-4">
        <span className="flex-1 text-sm" style={{ color: "var(--text)" }}>{label}</span>
        <span className="text-xs" style={{ color: "var(--text-muted)" }}>w: {Math.round(weight * 100)}%</span>
        <span
          className="w-8 text-right text-sm font-semibold tabular-nums"
          style={{ color }}
        >
          {s}
        </span>
      </div>
      <div className="mt-2 h-1 overflow-hidden rounded-full" style={{ backgroundColor: "var(--border-subtle)" }}>
        <div
          className="h-full rounded-full transition-all duration-500 ease-out"
          style={{ width: `${s}%`, backgroundColor: color }}
        />
      </div>
      {expanded && details.length > 0 && (
        <ul className="mt-2 space-y-1 pl-1">
          {details.map((d, i) => (
            <li key={i} className="flex items-start gap-2 text-xs" style={{ color: "var(--text-muted)" }}>
              <span className="mt-1.5 block h-1 w-1 shrink-0 rounded-full" style={{ backgroundColor: "var(--text-muted)" }} />
              {d}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ── Archetype Row ── */

function ArchetypeRow({ archetype, confidence, signals, secondary }: {
  archetype: string;
  confidence: number;
  signals: string[];
  secondary?: boolean;
}) {
  return (
    <div className="py-3" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
      <div className="flex items-center gap-3">
        <span className="text-xs font-medium" style={{ color: "var(--text-muted)" }}>
          {secondary ? m.deep_match_archetype_secondary() : m.deep_match_archetype_primary()}
        </span>
        <span className="text-sm font-semibold" style={{ color: "var(--text)" }}>
          {archetype.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
        </span>
        <span className="ml-auto text-xs tabular-nums" style={{ color: "var(--text-muted)" }}>
          {m.deep_match_confidence_pct({ pct: Math.round(confidence * 100) })}
        </span>
      </div>
      {signals.length > 0 && (
        <ul className="mt-1.5 space-y-0.5 pl-1">
          {signals.map((s, i) => (
            <li key={i} className="flex items-start gap-2 text-xs" style={{ color: "var(--text-muted)" }}>
              <span className="mt-1.5 block h-1 w-1 shrink-0 rounded-full" style={{ backgroundColor: "var(--text-muted)" }} />
              {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ── Main Page ── */

export function DeepMatchPage() {
  const { toast } = useToast();
  const [jobId, setJobId] = React.useState("");
  const [resumeId, setResumeId] = React.useState("");
  // EU AI Act Annex III human-oversight gate (KB-619): the user must
  // acknowledge the score is AI-assisted and that they review it before
  // acting on it, before any scoring is computed/actioned.
  const [oversightAck, setOversightAck] = React.useState(false);

  const jobsQuery = useQuery({ ...orpc.job.list.queryOptions(), retry: 1 });
  const resumesQuery = useQuery({ ...orpc.resume.list.queryOptions(), retry: 1 });

  const analyzeMutation = useMutation({
    ...orpc.deepMatch.analyze.mutationOptions(),
    onError: (err: Error) => {
      toast(err.message || m.deep_match_analysis_failed(), "error");
    },
    onSuccess: (result, variables) => {
      // Client transparency audit: record stable IDs and the numeric score only.
      // Raw resume/job text stays out of browser-visible audit events.
      logAnnexIIIAuditEvent({
        eventType: ANNEX_III_AUDIT_EVENTS.CANDIDATE_SCREENING_EVALUATED,
        candidateId: variables.resumeId,
        jobId: variables.jobId,
        screeningScore: result.scoring.overall,
      });
    },
  });

  const humanReviewMutation = useMutation({
    ...orpc.humanReview.set.mutationOptions(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: orpc.humanReview.get.key({}) });
    },
    onError: () => {
      toast("Human review request was recorded locally, but server sync failed.", "error");
    },
  });

  const result = analyzeMutation.data as DeepMatchResultShape | undefined;

  function handleAnalyze() {
    if (!jobId || !resumeId) {
      toast(m.deep_match_select_both(), "error");
      return;
    }
    // Human-oversight gate: require explicit acknowledgment before scoring.
    if (!oversightAck) {
      toast(m.deep_match_oversight_required(), "error");
      return;
    }
    analyzeMutation.mutate({ jobId, resumeId, humanConfirmed: true });
  }

  return (
    <div className="animate-fade-up mx-auto max-w-5xl pb-12">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1
            className="font-semibold tracking-tight"
            style={{ fontSize: "22px", color: "var(--text)" }}
          >
            {m.deep_match_page_title()}
          </h1>
          <p className="mt-1 text-sm" style={{ color: "var(--text-muted)" }}>
            {m.deep_match_page_description()}
          </p>
        </div>
      </div>

      {/* AI transparency disclaimer (EU AI Act Annex III / Article 50) — KB-619 */}
      <div
        role="note"
        data-compliance="eu-ai-act-art-50"
        className="mb-4 rounded-md border p-3 text-sm"
        style={{ borderColor: "var(--border-subtle)", background: "var(--surface-subtle, transparent)", color: "var(--text-muted)" }}
      >
        <p className="font-medium" style={{ color: "var(--text)" }}>
          {m.deep_match_ai_disclaimer_title()}
        </p>
        <p className="mt-1">{m.deep_match_ai_disclaimer_text()}</p>
      </div>

      {/* Input section — horizontal flex row */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1">
          <label
            htmlFor="dm-job"
            className="section-label mb-1.5 block"
          >
            {m.deep_match_job_label()}
          </label>
          <select
            id="dm-job"
            className="input w-full"
            value={jobId}
            onChange={(e) => setJobId(e.target.value)}
            disabled={jobsQuery.isLoading}
          >
            <option value="">{m.deep_match_select_job()}</option>
            {(jobsQuery.data ?? []).map((j: { id: string; title: string; company: string }) => (
              <option key={j.id} value={j.id}>
                {j.title} — {j.company}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <label
            htmlFor="dm-resume"
            className="section-label mb-1.5 block"
          >
            {m.deep_match_resume_label()}
          </label>
          <select
            id="dm-resume"
            className="input w-full"
            value={resumeId}
            onChange={(e) => setResumeId(e.target.value)}
            disabled={resumesQuery.isLoading}
          >
            <option value="">{m.deep_match_select_resume()}</option>
            {(resumesQuery.data ?? []).map((r: { id: string; title: string; isMaster?: boolean }) => (
              <option key={r.id} value={r.id}>
                {r.title}{r.isMaster ? " (Master)" : ""}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          className="btn btn-accent shrink-0 inline-flex items-center gap-2"
          onClick={handleAnalyze}
          disabled={analyzeMutation.isPending || !jobId || !resumeId || !oversightAck}
        >
          {analyzeMutation.isPending ? (
            <>
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden="true" />
              {m.deep_match_analyzing()}
            </>
          ) : (
            m.deep_match_analyze_btn()
          )}
        </button>
      </div>

      {/* Human-oversight acknowledgment gate (EU AI Act Annex III) — KB-619 */}
      <div className="mt-3">
        <label className="flex items-start gap-2 text-sm" style={{ color: "var(--text-muted)" }}>
          <input
            type="checkbox"
            className="mt-0.5"
            checked={oversightAck}
            onChange={(e) => setOversightAck(e.target.checked)}
          />
          <span>{m.deep_match_oversight_ack_label()}</span>
        </label>
      </div>

      {/* Loading State */}
      {analyzeMutation.isPending && (
        <div className="mt-8">
          <AnalysisSkeleton />
        </div>
      )}

      {/* Empty State */}
      {!result && !analyzeMutation.isPending && <EmptyState />}

      {/* Results */}
      {result && !analyzeMutation.isPending && (
        <div className="mt-8 space-y-0 animate-fade-up" style={{ borderTop: "1px solid var(--border-subtle)" }}>

          {/* Overall Score */}
          <section className="py-5" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
            <div className="flex items-center justify-between gap-4">
              <span className="section-label">{m.deep_match_overall_score()}</span>
              <div className="flex flex-wrap items-center justify-end gap-3">
                <CandidateScreeningBadge
                  compact
                  candidateId={resumeId || undefined}
                  jobId={jobId || undefined}
                  screeningScore={result.scoring.overall}
                  onHumanReviewRequested={() => {
                    humanReviewMutation.mutate({
                      preference: true,
                      source: "candidate_notice",
                    });
                  }}
                />
                <ScoreDisplay score={result.scoring.overall} />
              </div>
            </div>
          </section>

          {/* Archetype(s) */}
          <section className="py-5" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
            <h2 className="section-label mb-1">{m.deep_match_detected_archetype()}</h2>
            <ArchetypeRow
              archetype={result.scoring.archetype.archetype}
              confidence={result.scoring.archetype.confidence}
              signals={result.scoring.archetype.signals}
            />
            {result.scoring.secondaryArchetype && (
              <ArchetypeRow
                archetype={result.scoring.secondaryArchetype.archetype}
                confidence={result.scoring.secondaryArchetype.confidence}
                signals={result.scoring.secondaryArchetype.signals}
                secondary
              />
            )}
          </section>

          {/* 5 Dimensions */}
          <section className="py-5" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
            <h2 className="section-label mb-1">{m.deep_match_scoring_dimensions()}</h2>
            <DimensionRow {...result.scoring.skillsMatch} />
            <DimensionRow {...result.scoring.careerAlignment} />
            <DimensionRow {...result.scoring.compensationFit} />
            <DimensionRow {...result.scoring.culturalSignals} />
            <DimensionRow {...result.scoring.redFlags} />
          </section>

          {/* Requirement Mapping */}
          {result.requirementMapping.length > 0 && (
            <section className="py-5" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
              <h2 className="section-label mb-3">{m.deep_match_requirement_mapping()}</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                      <th className="pb-2 pr-4 text-left" style={{ color: "var(--text-muted)", fontWeight: 600, fontSize: "0.6875rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                        {m.deep_match_th_requirement()}
                      </th>
                      <th className="pb-2 pr-4 text-left" style={{ color: "var(--text-muted)", fontWeight: 600, fontSize: "0.6875rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                        {m.deep_match_th_your_evidence()}
                      </th>
                      <th className="pb-2 text-right" style={{ color: "var(--text-muted)", fontWeight: 600, fontSize: "0.6875rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                        {m.deep_match_th_match()}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.requirementMapping.map((rm, i) => (
                      <tr
                        key={i}
                        style={{ borderBottom: "1px solid var(--border-subtle)" }}
                      >
                        <td className="py-2.5 pr-4" style={{ color: "var(--text)" }}>{rm.requirement}</td>
                        <td className="py-2.5 pr-4" style={{ color: "var(--text-muted)" }}>
                          {rm.candidateEvidence || m.deep_match_no_evidence()}
                        </td>
                        <td className="py-2.5 text-right">
                          <span
                            className="text-xs font-medium tabular-nums"
                            style={{ color: matchStrengthColor(rm.matchStrength) }}
                          >
                            {rm.matchStrength}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Gap Analysis */}
          {result.gaps.length > 0 && (
            <section className="py-5" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
              <h2 className="section-label mb-1">{m.deep_match_gap_analysis()}</h2>
              {result.gaps.map((g, i) => (
                <div
                  key={i}
                  className="py-3"
                  style={{ borderBottom: i < result.gaps.length - 1 ? "1px solid var(--border-subtle)" : undefined }}
                >
                  <div className="flex items-start gap-3">
                    <span
                      className="mt-1.5 block h-1.5 w-1.5 shrink-0 rounded-full"
                      style={{ backgroundColor: severityDot(g.severity) }}
                      aria-hidden="true"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium" style={{ color: "var(--text)" }}>{g.gap}</span>
                        <span className="text-xs" style={{ color: severityDot(g.severity) }}>{g.severity}</span>
                      </div>
                      <p className="mt-0.5 text-xs" style={{ color: "var(--text-muted)" }}>{g.mitigation}</p>
                      {g.adjacentExperience && (
                        <p className="mt-0.5 text-xs italic" style={{ color: "var(--text-muted)" }}>
                          {m.deep_match_adjacent_label()}{g.adjacentExperience}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </section>
          )}

          {/* Positioning Advice */}
          <section className="py-5" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
            <h2 className="section-label mb-3">{m.deep_match_positioning_strategy()}</h2>
            <p className="text-sm font-medium" style={{ color: "var(--text)" }}>
              {result.positioning.framing}
            </p>
            <div className="mt-4">
              <span className="section-label">{m.deep_match_top_proof_points()}</span>
              <ul className="mt-2 space-y-1.5">
                {result.positioning.topProofPoints.map((pp, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm" style={{ color: "var(--text-muted)" }}>
                    <span className="mt-1.5 block h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: "var(--accent)" }} aria-hidden="true" />
                    {pp}
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-4">
              <span className="section-label">{m.deep_match_summary_angle()}</span>
              <p className="mt-1.5 text-sm" style={{ color: "var(--text-muted)" }}>
                {result.positioning.summaryAngle}
              </p>
            </div>
          </section>

          {/* Keyword Coverage */}
          <section className="py-5">
            <h2 className="section-label mb-3">{m.deep_match_keyword_coverage()}</h2>
            <KeywordCoverage keywords={result.keywords} />
          </section>
        </div>
      )}
    </div>
  );
}

/* ── Analysis Skeleton ── */

function AnalysisSkeleton() {
  return (
    <div className="space-y-5 animate-fade-up" style={{ borderTop: "1px solid var(--border-subtle)" }}>
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="py-5" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
          <Skeleton className="h-4 w-32 mb-3" />
          <Skeleton className="h-5 w-full" />
          {i > 1 && <Skeleton className="mt-2 h-1 w-full rounded-full" />}
        </div>
      ))}
    </div>
  );
}

/* ── Keyword Coverage ── */

function KeywordCoverage({ keywords }: {
  keywords: { extracted: string[]; present: string[]; missing: string[] };
}) {
  const total = keywords.extracted.length;
  const covered = keywords.present.length;
  const pct = total > 0 ? Math.round((covered / total) * 100) : 0;
  const pctColor = scoreColor(pct);

  return (
    <div>
      <div className="mb-3 flex items-center gap-3">
        <span className="text-sm" style={{ color: "var(--text-muted)" }}>
          {m.deep_match_keywords_covered({ covered, total })}
        </span>
        <span
          className="ml-auto text-sm font-semibold tabular-nums"
          style={{ color: pctColor }}
        >
          {pct}%
        </span>
      </div>
      <div className="mb-5 h-1 overflow-hidden rounded-full" style={{ backgroundColor: "var(--border-subtle)" }}>
        <div
          className="h-full rounded-full transition-all duration-500 ease-out"
          style={{ width: `${pct}%`, backgroundColor: pctColor }}
        />
      </div>

      {keywords.present.length > 0 && (
        <div className="mb-4" style={{ borderBottom: "1px solid var(--border-subtle)", paddingBottom: "1rem" }}>
          <span className="section-label">{m.deep_match_keywords_present()}</span>
          <ul className="mt-2 space-y-1">
            {keywords.present.map((kw, i) => (
              <li key={i} className="flex items-center gap-2 text-xs" style={{ color: "var(--text-muted)" }}>
                <span className="block h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: "var(--success)" }} aria-hidden="true" />
                {kw}
              </li>
            ))}
          </ul>
        </div>
      )}

      {keywords.missing.length > 0 && (
        <div>
          <span className="section-label">{m.deep_match_keywords_missing()}</span>
          <ul className="mt-2 space-y-1">
            {keywords.missing.map((kw, i) => (
              <li key={i} className="flex items-center gap-2 text-xs" style={{ color: "var(--text-muted)" }}>
                <span className="block h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: "var(--error)" }} aria-hidden="true" />
                {kw}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ── Inline type for result shape (avoids importing server types to client bundle) ── */

interface DeepMatchResultShape {
  scoring: {
    overall: number;
    skillsMatch: { score: number; weight: number; label: string; details: string[] };
    careerAlignment: { score: number; weight: number; label: string; details: string[] };
    compensationFit: { score: number; weight: number; label: string; details: string[] };
    culturalSignals: { score: number; weight: number; label: string; details: string[] };
    redFlags: { score: number; weight: number; label: string; details: string[] };
    archetype: { archetype: string; confidence: number; signals: string[] };
    secondaryArchetype?: { archetype: string; confidence: number; signals: string[] };
  };
  requirementMapping: { requirement: string; candidateEvidence: string | null; matchStrength: "strong" | "partial" | "none" }[];
  gaps: { gap: string; severity: string; mitigation: string; adjacentExperience?: string }[];
  positioning: { framing: string; topProofPoints: string[]; summaryAngle: string };
  keywords: { extracted: string[]; present: string[]; missing: string[] };
}
